import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import {
	SUPPORTED_LOCALES,
	SUPPORTED_LOCALE_CODES,
	DEFAULT_LOCALE,
	isValidLocale,
	getLocaleConfig,
} from './supported-locales';

const LOCALE_STORAGE_KEY = 'dddLocale';

/**
 * Get the initial language for i18next.
 * Priority: localStorage > WordPress user locale > navigator.language > fallback 'sk'
 */
function getInitialLanguage(): string {
	// 1. Check localStorage (user override)
	if (typeof window !== 'undefined') {
		try {
			const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
			if (storedLocale && isValidLocale(storedLocale)) {
				return storedLocale;
			}
		} catch {
			// Private mode or quota exceeded - continue to fallback
		}
	}

	// 2. Get from WordPress (user locale from PHP) - priority for authenticated users
	const wpLocale = (window as { dddData?: { locale?: string } }).dddData?.locale;
	if (wpLocale) {
		const shortCode = wpLocale.split('_')[0];
		if (isValidLocale(shortCode)) {
			return shortCode;
		}
	}

	// 3. Check browser language (for unauthenticated users / login page)
	if (typeof navigator !== 'undefined' && navigator.language) {
		const browserLang = navigator.language.split('-')[0]; // 'cs-CZ' → 'cs'
		if (isValidLocale(browserLang)) {
			return browserLang;
		}
	}

	// 4. Default fallback
	return DEFAULT_LOCALE;
}

const defaultLang = getInitialLanguage();

/**
 * Promise that resolves when i18n is fully initialized (including async language loading).
 * Use this to ensure translations are available before using t() outside React components.
 */
export const i18nReady: Promise<void> = new Promise((resolve) => {
	i18n.use(
		resourcesToBackend(
			(language: string, namespace: string) =>
				import(`../locales/${language}/${namespace}.json`)
		)
	)
		.use(initReactI18next)
		.init({
			lng: defaultLang,
			fallbackLng: DEFAULT_LOCALE,
			// SK is bundled inline (empty = use key as value), other languages load via backend
			partialBundledLanguages: true,
			resources: {
				sk: { translation: {} },
			},
			defaultNS: 'translation',
			// Disable namespace separator (:) - we use Slovak text as keys which may contain colons
			nsSeparator: false,
			interpolation: { escapeValue: false },
			react: { useSuspense: true },
		})
		.then(() => {
			// Provide Slovak plural forms for day label (source language has no PO/JSON).
			i18n.addResourceBundle(
				'sk',
				'translation',
				{
					'{{count}} deň_one': '{{count}} deň',
					'{{count}} deň_few': '{{count}} dni',
					'{{count}} deň_other': '{{count}} dní',
				},
				true,
				true
			);
			resolve();
		});
});

// Expose i18n to window for debugging (language switching in console)
if (typeof window !== 'undefined') {
	(window as Window & { i18n?: typeof i18n }).i18n = i18n;
}

/**
 * Change the current language.
 * Stores the preference in localStorage for persistence.
 *
 * @param locale - Short locale code (e.g., 'sk', 'cs')
 */
export async function changeLanguage(locale: string): Promise<void> {
	// Store in localStorage for persistence
	localStorage.setItem(LOCALE_STORAGE_KEY, locale);

	// Change i18next language (triggers re-render of all translated components)
	await i18n.changeLanguage(locale);
}

/**
 * Get the current language.
 */
export function getCurrentLanguage(): string {
	return i18n.language;
}

/**
 * Get the date-fns locale for the current language.
 * Falls back to Slovak if the current language is not supported.
 */
export function getDateLocale() {
	return getLocaleConfig(i18n.language).dateFns;
}

/**
 * Get the Intl locale string for number formatting.
 * Falls back to Slovak if the current language is not supported.
 *
 * @example
 * value.toLocaleString(getNumberLocale())
 */
export function getNumberLocale(): string {
	return getLocaleConfig(i18n.language).intl;
}

/**
 * Get the localStorage key for locale (for external use if needed)
 */
export { LOCALE_STORAGE_KEY };

// Re-export locale utilities for convenience
export { SUPPORTED_LOCALES, SUPPORTED_LOCALE_CODES, isValidLocale };

/**
 * Mark a string for translation extraction without translating it at runtime.
 * Used for presets/config labels that are translated later via t(...).
 */
export const gettext_noop = (value: string): string => value;

/**
 * Mark singular and plural forms for gettext extraction without translating at runtime.
 * The singular form remains the i18next key; generated JSON provides CLDR plural suffixes.
 */
export const gettext_n_noop = (singular: string, _plural: string): string => singular;

export default i18n;
